import { useState } from "react";

const C = {
  ink:"#1C1A17", cream:"#F6F2EA", warm:"#FDFAF5",
  muted:"#9A9186", border:"#E4DDD3", tc:"#C4704A",
  sage:"#7B9E87", renew:"#B8845A", rebuild:"#7B7BAE",
};

const KLAVIYO_KEY = "VyDDj7";
const KLAVIYO_LIST = "SwEqzK";

async function saveToKlaviyo(email, stage, source) {
  const stageMap = {
    restore:"Stress-related & Postpartum Shedding",
    renew:"Hormonal & Transitional Thinning",
    rebuild:"Advanced Density Restoration",
    unsure:"Not Sure Yet",
    "":"Not Selected",
  };

  const refCode = "ROOT" + String(email.toLowerCase().split("").reduce((a,c)=>a+c.charCodeAt(0),0)%9000+1000);
  const mappedStage = stageMap[stage] || "Not Selected";
  const mappedSource = source || "Not Selected";

  // Step 1 — Create/update the profile with properties first
  try {
    await fetch(
      `https://a.klaviyo.com/client/profiles/?company_id=${KLAVIYO_KEY}`,
      {
        method:"POST",
        headers:{
          "content-type":"application/vnd.api+json",
          "revision":"2024-10-15",
        },
        body:JSON.stringify({
          data:{
            type:"profile",
            attributes:{
              email,
              properties:{
                WaitlistStage: mappedStage,
                WaitlistSource: mappedSource,
                WaitlistDate: new Date().toISOString(),
                ReferralCode: refCode,
              },
            },
          },
        }),
      }
    );
  } catch(e) {
    console.warn("Profile create/update failed:", e.message);
  }

  // Step 2 — Subscribe profile to the list
  try {
    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_KEY}`,
      {
        method:"POST",
        headers:{
          "content-type":"application/vnd.api+json",
          "revision":"2024-10-15",
        },
        body:JSON.stringify({
          data:{
            type:"subscription",
            attributes:{
              list_id:KLAVIYO_LIST,
              custom_source:"rootscalp.com waitlist",
              profile:{
                data:{
                  type:"profile",
                  attributes:{
                    email,
                    properties:{
                      WaitlistStage: mappedStage,
                      WaitlistSource: mappedSource,
                      WaitlistDate: new Date().toISOString(),
                      ReferralCode: refCode,
                    },
                  },
                },
              },
            },
          },
        }),
      }
    );
    if (res.status === 202 || res.status === 200) return true;
    console.warn("Subscription status:", res.status, await res.text().catch(()=>""));
  } catch(e) {
    console.warn("Subscription failed:", e.message);
  }

  // Step 3 — Legacy fallback with all properties
  try {
    const form = new FormData();
    form.append("email", email);
    form.append("g", KLAVIYO_LIST);
    form.append("$fields", "$source,WaitlistStage,WaitlistSource,WaitlistDate,ReferralCode");
    form.append("$source", "rootscalp.com waitlist");
    form.append("WaitlistStage", mappedStage);
    form.append("WaitlistSource", mappedSource);
    form.append("WaitlistDate", new Date().toISOString());
    form.append("ReferralCode", refCode);
    const res2 = await fetch(
      "https://manage.kmail-lists.com/ajax/subscriptions/subscribe",
      { method:"POST", headers:{"cache-control":"no-cache"}, body:form }
    );
    const json = await res2.json().catch(()=>({}));
    if (json.success || res2.ok) return true;
  } catch(e) {
    console.warn("Legacy fallback failed:", e.message);
  }

  throw new Error("All Klaviyo methods failed");
}

function generateReferralCode(email) {
  const hash = email.split("").reduce((a,c) => a + c.charCodeAt(0), 0);
  return "ROOT" + String(hash).padStart(4,"0").slice(-4).toUpperCase();
}

const REFERRAL_TIERS = [
  { count:5,  reward:"Free Scalp Massager",     value:"Worth £22", icon:"🎁" },
  { count:10, reward:"Founding Member Gift Box", value:"Worth £45", icon:"📦" },
  { count:25, reward:"Free Full Serum",          value:"Worth £65", icon:"✨" },
];

function ReferralScreen({ email, position }) {
  const [copied, setCopied] = useState(false);
  const [referrals] = useState(0);
  const code = generateReferralCode(email);
  const link = `https://rootscalp.com/join?ref=${code}`;
  const copy = () => { navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); }); };
  const nextTier = REFERRAL_TIERS.find(t => t.count > referrals) || REFERRAL_TIERS[REFERRAL_TIERS.length-1];
  const toNext = Math.max(0, nextTier.count - referrals);
  return (
    <div>
      <div style={{background:`${C.sage}15`,border:`1.5px solid ${C.sage}40`,padding:"32px",borderRadius:3,textAlign:"center",marginBottom:2}}>
        <div style={{fontSize:44,marginBottom:14}}>✓</div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:300,color:C.ink,marginBottom:8,lineHeight:1.1}}>You're in.</div>
        <div style={{display:"inline-block",background:C.sage,color:"white",padding:"7px 22px",borderRadius:2,fontSize:12,fontWeight:600,letterSpacing:"1px",marginBottom:14}}>#{position} on the waitlist</div>
        <p style={{fontSize:14,color:C.muted,lineHeight:1.8,maxWidth:400,margin:"0 auto"}}>We'll email you the moment RŌOT launches — before anyone else. You've secured founding member access. Now move up by sharing your link.</p>
      </div>
      <div style={{background:C.ink,padding:"28px 24px"}}>
        <div style={{fontSize:10,letterSpacing:"2.5px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:10}}>Move Up The Waitlist</div>
        <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontWeight:300,color:C.cream,marginBottom:8}}>Refer friends.<br/>Earn real rewards.</h3>
        <p style={{fontSize:13,color:"rgba(246,242,234,.5)",lineHeight:1.75,marginBottom:20}}>Every friend who joins through your link moves you higher and unlocks something worth having.</p>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:"rgba(246,242,234,.35)",marginBottom:8,letterSpacing:"0.5px"}}>Your personal referral link</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}} className="root-ref-row">
            <div style={{flex:"1 1 160px",minWidth:0,padding:"12px 14px",background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",fontSize:11,color:"rgba(246,242,234,.7)",borderRadius:3,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",fontFamily:"monospace"}}>{link}</div>
            <button onClick={copy} style={{flex:"1 1 auto",background:copied?C.sage:C.tc,color:"white",padding:"12px 18px",border:"none",borderRadius:3,fontSize:11,letterSpacing:"1px",textTransform:"uppercase",fontWeight:500,cursor:"pointer",transition:"background .2s"}}>{copied?"Copied ✓":"Copy"}</button>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",padding:"14px 16px",borderRadius:3,marginBottom:20}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:40,fontWeight:300,color:C.tc,lineHeight:1}}>{referrals}</div>
          <div>
            <div style={{fontSize:13,color:C.cream,fontWeight:500}}>{referrals===0?"No referrals yet":`${referrals} friend${referrals===1?"":"s"} referred`}</div>
            <div style={{fontSize:12,color:"rgba(246,242,234,.4)",marginTop:2}}>{toNext} more to unlock: {nextTier.reward}</div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {REFERRAL_TIERS.map(tier => {
            const unlocked = referrals >= tier.count;
            const current = nextTier.count === tier.count && !unlocked;
            return (
              <div key={tier.count} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 14px",background:unlocked?`${C.sage}20`:current?"rgba(255,255,255,.06)":"rgba(255,255,255,.02)",border:`1px solid ${unlocked?C.sage+"50":current?"rgba(255,255,255,.15)":"rgba(255,255,255,.06)"}`,borderRadius:3,opacity:unlocked?1:current?1:0.55}}>
                <span style={{fontSize:22,flexShrink:0}}>{tier.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:unlocked?C.sage:C.cream,fontWeight:current?500:400}}>{tier.reward}</div>
                  <div style={{fontSize:11,color:"rgba(246,242,234,.35)",marginTop:2}}>{tier.count} referrals · {tier.value}</div>
                </div>
                <div style={{fontSize:10,letterSpacing:"1px",textTransform:"uppercase",color:unlocked?C.sage:current?C.tc:"rgba(246,242,234,.2)",fontWeight:500,flexShrink:0}}>{unlocked?"Unlocked ✓":current?`${toNext} to go`:`${tier.count-referrals} away`}</div>
              </div>
            );
          })}
        </div>
        <div style={{marginTop:4}}>
          <div style={{fontSize:9,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(246,242,234,.3)",marginBottom:10}}>Share your link</div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}} className="root-share-row">
            <a href={`https://wa.me/?text=${encodeURIComponent("I just joined the RŌOT waitlist — scalp care built for your stage with AI progress tracking. Join me: "+link)}`} target="_blank" rel="noreferrer" style={{flex:1,minWidth:"140px",padding:"12px 8px",textAlign:"center",background:"#25D366",color:"white",fontSize:12,fontWeight:500,textDecoration:"none",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>💬 WhatsApp</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent("I just joined the RŌOT waitlist — scalp care built for your stage.")}`} target="_blank" rel="noreferrer" style={{flex:1,minWidth:"140px",padding:"12px 8px",textAlign:"center",background:"#1877F2",color:"white",fontSize:12,fontWeight:500,textDecoration:"none",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>📘 Facebook</a>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}} className="root-share-row">
            <button onClick={()=>navigator.clipboard.writeText(link).then(()=>alert("Link copied! Paste it into your Instagram story or bio."))} style={{flex:1,minWidth:"140px",padding:"12px 8px",textAlign:"center",background:"linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",color:"white",fontSize:12,fontWeight:500,border:"none",borderRadius:3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>📷 Instagram</button>
            <a href={`sms:?body=${encodeURIComponent("I just joined the RŌOT waitlist — scalp care built for your stage. Join me: "+link)}`} style={{flex:1,minWidth:"140px",padding:"12px 8px",textAlign:"center",background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.15)",color:"rgba(246,242,234,.7)",fontSize:12,fontWeight:500,textDecoration:"none",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>💌 Text a Friend</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmailForm({ dark=false, label="Secure My Spot", showStage=true }) {
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [position, setPosition] = useState(null);
  const [joinedEmail, setJoinedEmail] = useState("");

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed.includes("@") || !trimmed.includes(".")) {
      setErr("Please enter a valid email address."); return;
    }
    setStatus("loading"); setErr("");
    try {
      await saveToKlaviyo(trimmed, stage, source);
      setPosition(Math.floor(Math.random() * 300) + 1800);
      setJoinedEmail(trimmed);
      setStatus("success");
    } catch(e) {
      setStatus("idle");
      setErr("Something went wrong — please try again.");
    }
  };

  if (status === "success") return <ReferralScreen email={joinedEmail} position={position}/>;

  const stages = [
    [C.sage,    "restore", "Stress-related & postpartum shedding"],
    [C.renew,   "renew",   "Hormonal & transitional thinning"],
    [C.rebuild, "rebuild", "Advanced density restoration"],
    [C.muted,   "unsure",  "Not sure — I want to learn more"],
  ];

  return (
    <div>
      {showStage && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,letterSpacing:"2px",textTransform:"uppercase",color:dark?"rgba(246,242,234,.4)":C.muted,fontWeight:500,marginBottom:10}}>Which sounds like you? <span style={{opacity:.6,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {stages.map(([color,val,txt])=>(
              <div key={val} onClick={()=>setStage(stage===val?"":val)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",border:`1.5px solid ${stage===val?color:dark?"rgba(246,242,234,.12)":C.border}`,background:stage===val?`${color}18`:dark?"rgba(255,255,255,.03)":"white",cursor:"pointer",borderRadius:3,transition:"all .15s"}}>
                <div style={{width:18,height:18,borderRadius:3,flexShrink:0,border:`1.5px solid ${stage===val?color:dark?"rgba(246,242,234,.2)":C.border}`,background:stage===val?color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                  {stage===val&&<span style={{color:"white",fontSize:10,fontWeight:700}}>✓</span>}
                </div>
                <span style={{fontSize:13,color:stage===val?(dark?C.cream:C.ink):dark?"rgba(246,242,234,.5)":C.muted,fontWeight:stage===val?500:400,lineHeight:1.4}}>{txt}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {showStage && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,letterSpacing:"2px",textTransform:"uppercase",color:dark?"rgba(246,242,234,.4)":C.muted,fontWeight:500,marginBottom:10}}>How did you hear about us? <span style={{opacity:.6,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {["TikTok","Instagram","Facebook","Reddit","Friend","Google","Other"].map(s=>(
              <div key={s} onClick={()=>setSource(source===s?"":s)} style={{padding:"8px 14px",border:`1.5px solid ${source===s?C.tc:dark?"rgba(246,242,234,.15)":C.border}`,background:source===s?`${C.tc}18`:dark?"rgba(255,255,255,.03)":"white",cursor:"pointer",borderRadius:20,fontSize:12,color:source===s?(dark?C.cream:C.ink):dark?"rgba(246,242,234,.45)":C.muted,fontWeight:source===s?500:400,transition:"all .15s"}}>{s}</div>
            ))}
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}} className="root-form-row">
        <input type="email" placeholder="your@email.com" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&submit()} style={{flex:"1 1 180px",minWidth:0,width:"100%",padding:"14px 14px",border:`1.5px solid ${err?"#fca5a5":dark?"rgba(246,242,234,.15)":C.border}`,background:dark?"rgba(255,255,255,.06)":C.cream,fontSize:14,outline:"none",borderRadius:3,color:dark?C.cream:C.ink,fontFamily:"inherit",boxSizing:"border-box"}}/>
        <button onClick={submit} disabled={status==="loading"} style={{flex:"1 1 100%",padding:"14px 20px",border:"none",borderRadius:3,fontSize:11,letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:500,whiteSpace:"nowrap",cursor:status==="loading"?"not-allowed":"pointer",fontFamily:"inherit",background:status==="loading"?"#555":C.ink,color:C.cream}}>{status==="loading"?"Joining…":label+" →"}</button>
      </div>
      {err&&<div style={{fontSize:12,color:"#dc2626",marginTop:6}}>{err}</div>}
      <div style={{fontSize:11,marginTop:10,color:dark?"rgba(246,242,234,.25)":C.muted,lineHeight:1.6}}>No spam. One email when we launch. Unsubscribe any time.</div>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{borderBottom:`1px solid ${C.border}`}}>
      <div onClick={()=>setOpen(!open)} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"22px 0",cursor:"pointer",gap:16}}>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:400,lineHeight:1.35,color:C.ink}}>{q}</div>
        <div style={{width:26,height:26,borderRadius:"50%",border:`1.5px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:15,color:C.muted,transition:"transform .2s",transform:open?"rotate(45deg)":"none"}}>+</div>
      </div>
      {open&&<div style={{fontSize:14,color:C.muted,lineHeight:1.9,paddingBottom:22,fontWeight:300}}>{a}</div>}
    </div>
  );
}

function ProgressMockup() {
  const [active, setActive] = useState(0);
  const tabs = ["Dashboard","Check-In","AI Report"];
  return (
    <div style={{background:C.ink,borderRadius:6,overflow:"hidden",width:"100%",maxWidth:300,boxShadow:"0 20px 60px rgba(0,0,0,.3)"}}>
      <div style={{display:"flex",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
        {tabs.map((t,i)=>(
          <button key={t} onClick={()=>setActive(i)} style={{flex:1,padding:"10px 4px",background:i===active?"rgba(255,255,255,.06)":"transparent",border:"none",borderBottom:i===active?`2px solid ${C.tc}`:"2px solid transparent",color:i===active?C.cream:"rgba(246,242,234,.35)",fontSize:8,letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .15s"}}>{t}</button>
        ))}
      </div>
      {active===0&&(
        <div style={{padding:"16px"}}>
          <div style={{fontSize:7.5,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(246,242,234,.3)",marginBottom:12}}>Root Progress™</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:2,marginBottom:2}}>
            {[{l:"Stage",v:"Restore",c:C.sage},{l:"Day",v:"Day 34",c:C.tc},{l:"Root Score™",v:"82/100",c:C.sage},{l:"Next Check-In",v:"26 days",c:C.renew}].map(card=>(
              <div key={card.l} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",padding:"10px"}}>
                <div style={{fontSize:7,letterSpacing:"1px",textTransform:"uppercase",color:"rgba(246,242,234,.3)",marginBottom:5}}>{card.l}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:19,fontWeight:300,color:card.c}}>{card.v}</div>
              </div>
            ))}
          </div>
          <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",padding:"12px"}}>
            <div style={{fontSize:7,letterSpacing:"1px",textTransform:"uppercase",color:"rgba(246,242,234,.3)",marginBottom:10}}>Your Journey</div>
            <div style={{display:"flex",justifyContent:"space-between",position:"relative"}}>
              <div style={{position:"absolute",top:9,left:10,right:10,height:2,background:"rgba(255,255,255,.08)"}}/>
              <div style={{position:"absolute",top:9,left:10,width:"38%",height:2,background:C.sage}}/>
              {[{l:"Day 1",d:true},{l:"Day 30",d:true},{l:"Day 60",d:false},{l:"Day 90",d:false}].map(cp=>(
                <div key={cp.l} style={{textAlign:"center",position:"relative",zIndex:1}}>
                  <div style={{width:18,height:18,borderRadius:"50%",margin:"0 auto 5px",background:cp.d?C.sage:"rgba(255,255,255,.08)",border:cp.d?`1.5px solid ${C.sage}`:"1.5px solid rgba(255,255,255,.12)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {cp.d&&<span style={{color:"white",fontSize:8,fontWeight:700}}>✓</span>}
                  </div>
                  <div style={{fontSize:7,color:cp.d?"rgba(246,242,234,.6)":"rgba(246,242,234,.2)"}}>{cp.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {active===1&&(
        <div style={{padding:"16px"}}>
          <div style={{fontSize:7.5,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(246,242,234,.3)",marginBottom:12}}>Day 30 Check-In</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            <div style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",borderRadius:3,padding:"10px",textAlign:"center"}}>
              <div style={{width:"100%",height:56,background:"rgba(123,158,135,.2)",borderRadius:2,marginBottom:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>📷</div>
              <div style={{fontSize:7,color:"rgba(246,242,234,.4)"}}>Day 1 Baseline</div>
            </div>
            <div style={{background:"rgba(255,255,255,.04)",border:`1.5px dashed ${C.tc}`,borderRadius:3,padding:"10px",textAlign:"center",cursor:"pointer"}}>
              <div style={{width:"100%",height:56,background:"rgba(196,112,74,.08)",borderRadius:2,marginBottom:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>+</div>
              <div style={{fontSize:7,color:C.tc}}>Upload Day 30</div>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",padding:"10px",borderRadius:3}}>
            <div style={{fontSize:7,color:"rgba(246,242,234,.4)",marginBottom:8}}>How is your shedding today?</div>
            <div style={{display:"flex",gap:3}}>
              {["Much less","Less","Same","More"].map((o,i)=>(
                <div key={o} style={{flex:1,padding:"5px 2px",background:i===1?C.sage:"rgba(255,255,255,.05)",border:`1px solid ${i===1?C.sage:"rgba(255,255,255,.1)"}`,borderRadius:2,textAlign:"center",fontSize:6,color:i===1?"white":"rgba(246,242,234,.45)",cursor:"pointer"}}>{o}</div>
              ))}
            </div>
          </div>
        </div>
      )}
      {active===2&&(
        <div style={{padding:"16px"}}>
          <div style={{fontSize:7.5,letterSpacing:"2px",textTransform:"uppercase",color:C.sage,marginBottom:10}}>Day 30 AI Report</div>
          <div style={{background:`${C.sage}18`,border:`1px solid ${C.sage}30`,borderRadius:3,padding:"10px",marginBottom:8}}>
            <div style={{fontSize:8,fontWeight:500,color:C.sage,marginBottom:4}}>Root Score™ improved</div>
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:300,color:C.sage}}>82</div>
              <div style={{fontSize:9,color:"rgba(246,242,234,.5)"}}>/ 100 · up from 64</div>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",padding:"10px",borderRadius:3,marginBottom:6}}>
            <div style={{fontSize:7,fontWeight:500,color:C.cream,marginBottom:5}}>What the AI sees</div>
            <p style={{fontSize:6.5,color:"rgba(246,242,234,.5)",lineHeight:1.75,fontStyle:"italic"}}>"At this stage in the Restore journey, many customers report a shift in daily shedding. Your consistency is exactly what this phase calls for..."</p>
          </div>
          <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",padding:"10px",borderRadius:3}}>
            <div style={{fontSize:7,fontWeight:500,color:C.cream,marginBottom:6}}>Focus for next 30 days</div>
            {["Morning serum — temples first","60-second scalp massage daily","Same lighting for Day 60 photo"].map(tip=>(
              <div key={tip} style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:5}}>
                <div style={{width:4,height:4,borderRadius:"50%",background:C.tc,flexShrink:0,marginTop:3}}/>
                <div style={{fontSize:6.5,color:"rgba(246,242,234,.5)",lineHeight:1.5}}>{tip}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WaitlistPage() {
  const socialProof = "2,000+";
  const P = "clamp(56px,8vw,96px) clamp(20px,6vw,80px)";

  return (
    <div style={{background:C.warm,fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        *{box-sizing:border-box;}
        @media(max-width:479px){
          .root-form-row{flex-direction:column!important;}
          .root-form-row input{width:100%!important;min-width:0!important;max-height:52px!important;font-size:16px!important;}
          .root-form-row button{width:100%!important;flex:none!important;max-height:52px!important;}
          .root-ref-row{flex-direction:column!important;}
          .root-ref-row div{width:100%!important;}
          .root-ref-row button{width:100%!important;}
          .root-share-row{flex-direction:column!important;}
          .root-share-row a,.root-share-row button{width:100%!important;min-width:0!important;}
          .root-grid-3{grid-template-columns:1fr!important;}
          .root-grid-4{grid-template-columns:1fr 1fr!important;}
          .root-journey-grid{grid-template-columns:1fr 1fr!important;}
          .root-stats-grid{grid-template-columns:1fr 1fr!important;}
          .root-challenge-grid{grid-template-columns:1fr 1fr!important;}
          .root-progress-grid{grid-template-columns:1fr!important;}
        }
        @media(max-width:360px){
          .root-grid-4{grid-template-columns:1fr!important;}
          .root-journey-grid{grid-template-columns:1fr!important;}
          .root-challenge-grid{grid-template-columns:1fr!important;}
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={{background:C.ink,padding:P,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,pointerEvents:"none",background:"radial-gradient(ellipse at 75% 25%, rgba(123,158,135,.12) 0%, transparent 55%), radial-gradient(ellipse at 15% 85%, rgba(196,112,74,.1) 0%, transparent 50%)"}}/>
        <div style={{maxWidth:680,margin:"0 auto",position:"relative",zIndex:1}}>

          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:`${C.tc}22`,padding:"7px 16px",borderRadius:2,marginBottom:36}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:C.tc}}/>
            <span style={{fontSize:10,letterSpacing:"2.5px",textTransform:"uppercase",color:C.tc,fontWeight:500}}>Launching Soon · {socialProof} Women Already Waiting</span>
          </div>

          {/* HERO HEADLINE — emotional, not functional */}
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(40px,7vw,84px)",fontWeight:300,lineHeight:1.05,color:C.cream,marginBottom:22}}>
            When your hair changes,<br/>
            <em style={{color:C.tc}}>confidence changes too.</em>
          </h1>

          <p style={{fontSize:"clamp(15px,2vw,18px)",color:"rgba(246,242,234,.7)",lineHeight:1.85,fontWeight:300,maxWidth:520,marginBottom:8}}>
            For women navigating stress-related shedding, postpartum hair changes, and hormonal thinning.
          </p>
          <p style={{fontSize:"clamp(14px,1.8vw,17px)",color:"rgba(246,242,234,.5)",lineHeight:1.75,fontWeight:300,maxWidth:500,marginBottom:14}}>
            A 90-day scalp recovery system — matched to your biology, tracked by AI.
          </p>
          <p style={{fontSize:"clamp(13px,1.6vw,15px)",color:C.tc,lineHeight:1.6,fontWeight:500,letterSpacing:"0.5px",maxWidth:480,marginBottom:44}}>
            Launching soon. Join the founding members waitlist.
          </p>

          <div style={{background:"rgba(246,242,234,.06)",border:"1px solid rgba(246,242,234,.14)",padding:"26px",borderRadius:3,marginBottom:24}}>
            <div style={{fontSize:10,letterSpacing:"2.5px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:16}}>Join the First 500 Founding Members — Get:</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:22}}>
              {[
                ["⚡","Early access — before anyone else sees the price"],
                ["💰","Founding member pricing — locked for 48 hours at launch"],
                ["🧠","Root Progress™ free — your personal AI scalp coach, built in"],
                ["🏆","Automatic entry into the £10,000 Root Challenge"],
                ["🎁","Free Scalp Massager (worth £22) with your first order"],
              ].map(([icon,text])=>(
                <div key={text} style={{display:"flex",alignItems:"flex-start",gap:12,fontSize:13,color:"rgba(246,242,234,.75)",lineHeight:1.5}}>
                  <span style={{fontSize:16,flexShrink:0,marginTop:1}}>{icon}</span><span>{text}</span>
                </div>
              ))}
            </div>
            <EmailForm dark label="Claim My Founding Spot"/>
          </div>

          <div style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:"rgba(246,242,234,.3)"}}>
            <div style={{display:"flex"}}>{["#C4704A","#7B9E87","#B8845A","#7B7BAE","#9A9186"].map((bg,i)=><div key={i} style={{width:26,height:26,borderRadius:"50%",background:bg,border:"2px solid #1C1A17",marginLeft:i===0?0:-7}}/>)}</div>
            <span>{socialProof} women already on the waitlist</span>
          </div>
        </div>
      </div>

      {/* ── THE QUESTION EVERY CUSTOMER ASKS ── */}
      <div style={{background:C.cream,padding:"clamp(40px,6vw,72px) clamp(20px,6vw,80px)"}}>
        <div style={{maxWidth:680,margin:"0 auto",textAlign:"center"}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(26px,4vw,44px)",fontWeight:300,lineHeight:1.2,color:C.ink,marginBottom:16}}>
            "How do I know it's actually working?"
          </h2>
          <p style={{fontSize:"clamp(14px,1.8vw,17px)",color:C.muted,lineHeight:1.85,maxWidth:520,margin:"0 auto",fontWeight:300}}>
            That's the question every woman with hair loss asks after buying a serum. Most brands have no answer. They sell you a bottle and disappear. RŌOT answers it at Day 30, Day 60, and Day 90 — with your own photos, your own data, and an AI that shows you exactly what's changed.
          </p>
        </div>
      </div>

      {/* ── ROOT PROGRESS — THE ACE CARD ── */}
      <div style={{padding:P,background:C.warm}}>
        <div style={{maxWidth:920,margin:"0 auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:"clamp(40px,6vw,72px)",alignItems:"center"}} className="root-progress-grid">
            <div>
              <div style={{fontSize:10,letterSpacing:"3px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:14}}>Root Progress™ — Included Free With Every Order</div>
              <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(28px,3.5vw,46px)",fontWeight:300,lineHeight:1.1,marginBottom:16}}>Every other brand sells you<br/>a bottle and moves on.<br/><em style={{color:C.tc}}>RŌOT doesn't.</em></h2>
              <p style={{fontSize:14,color:C.muted,lineHeight:1.9,marginBottom:10}}>Most people quit at week three because they can't see anything and assume it's not working. Hair growth takes 90 days. Root Progress™ keeps you in the journey — with real evidence that something is happening beneath the surface.</p>
              <p style={{fontSize:14,color:C.muted,lineHeight:1.9,marginBottom:28}}>It's the closest thing to a private trichology consultation — documented, personalised, and included with every RŌOT order. No other brand does this.</p>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {[
                  ["📷","Upload your photo","Day 1, 30, 60, and 90. Same spot. Same angle. The AI handles the rest."],
                  ["🧠","Receive your personalised report","Based on your actual photos and check-in data — not generic advice."],
                  ["📊","Track your Root Score™","A real number that improves as your scalp recovers. You can see it working."],
                  ["🏆","Enter the Root Challenge","Document 90 days. Win part of £10,000. Open to every customer."],
                ].map(([icon,title,desc])=>(
                  <div key={title} style={{display:"flex",gap:14,alignItems:"flex-start",padding:"15px 16px",borderLeft:`3px solid ${C.tc}`,background:C.cream,marginBottom:2}}>
                    <span style={{fontSize:20,flexShrink:0,marginTop:1}}>{icon}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,marginBottom:3,color:C.ink}}>{title}</div>
                      <div style={{fontSize:12,color:C.muted,lineHeight:1.65}}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
              <ProgressMockup/>
              <div style={{fontSize:10,color:C.muted,letterSpacing:"1.5px",textTransform:"uppercase",textAlign:"center"}}>Tap the tabs to explore Root Progress™</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── THE THREE STAGES ── */}
      <div style={{padding:P,background:C.cream}}>
        <div style={{maxWidth:880,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:52}}>
            <div style={{fontSize:10,letterSpacing:"3px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:14}}>Three Formulas. One Is Built For You.</div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(28px,4vw,48px)",fontWeight:300,lineHeight:1.15,marginBottom:16}}>Which one sounds like your life right now?</h2>
            <p style={{fontSize:14,color:C.muted,lineHeight:1.85,maxWidth:520,margin:"0 auto"}}>Stress-related shedding, hormonal thinning, and post-menopause density loss are three completely different biological problems. Most brands treat them all the same. RŌOT doesn't.</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:2,marginBottom:20}} className="root-grid-3">
            {[
              {color:C.sage, name:"Restore", desc:"Stress-related & postpartum shedding", icon:"🌿",
               who:"You're postpartum, burned out, or going through something hard — and your hair is coming out in handfuls. Nobody warned you it would be this much.",
               promise:"Restore is built to help your scalp recover from the cortisol and oestrogen shifts driving your shedding. The 90-day system is designed around exactly this."},
              {color:C.renew, name:"Renew", desc:"Hormonal & transitional thinning", icon:"🌸",
               who:"Your parting has quietly been getting wider for a couple of years. Your ponytail feels thinner. You're in perimenopause and nobody joined the dots for you.",
               promise:"Renew targets the DHT conversion and hormonal shift causing this specific type of thinning. Not a general serum — a formula built for this phase."},
              {color:C.rebuild, name:"Rebuild", desc:"Advanced density restoration", icon:"🪨",
               who:"You've watched your hair get thinner for years and accepted it as inevitable. You've tried things before. You're open to being proved wrong.",
               promise:"Rebuild uses the most advanced actives available for dormant follicle reactivation. Built for women who want serious results — not just less shedding."},
            ].map(st=>(
              <div key={st.name} style={{background:C.warm,borderTop:`4px solid ${st.color}`,padding:"30px 24px",display:"flex",flexDirection:"column"}}>
                <div style={{fontSize:30,marginBottom:14}}>{st.icon}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontWeight:300,color:C.ink,marginBottom:5}}>RŌOT {st.name}</div>
                <div style={{fontSize:9,letterSpacing:"1.5px",textTransform:"uppercase",color:st.color,fontWeight:500,marginBottom:18,lineHeight:1.5}}>{st.desc}</div>
                <div style={{background:`${st.color}0D`,border:`1px solid ${st.color}22`,padding:"14px 16px",marginBottom:16,borderRadius:2,flex:1}}>
                  <p style={{fontSize:13,color:"#5a5650",lineHeight:1.8,fontStyle:"italic",marginBottom:10}}>{st.who}</p>
                  <p style={{fontSize:12.5,color:C.muted,lineHeight:1.75}}>{st.promise}</p>
                </div>
                <div style={{fontSize:11,color:C.muted,borderTop:`1px solid ${C.border}`,paddingTop:12,lineHeight:1.5}}>Join the waitlist — be first when we launch</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 90-DAY JOURNEY ── */}
      <div style={{background:C.ink,padding:P}}>
        <div style={{maxWidth:880,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:52}}>
            <div style={{fontSize:10,letterSpacing:"3px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:14}}>What 90 Days Actually Looks Like</div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(28px,4vw,48px)",fontWeight:300,lineHeight:1.1,color:C.cream,marginBottom:16}}>
              Hair takes 90 days to respond.<br/>We track every one of them.
            </h2>
            <p style={{fontSize:14,color:"rgba(246,242,234,.45)",lineHeight:1.85,maxWidth:500,margin:"0 auto",fontWeight:300}}>Most people quit at week three because they can't see anything yet. That's not failure — that's just how hair biology works. Root Progress™ keeps you in the journey until the results are visible.</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:2}} className="root-journey-grid">
            {[
              {day:"Day 1", color:C.sage, icon:"📷", title:"Your baseline", body:"Upload your first photo. The AI records your starting point — scalp density, parting width, condition. Everything gets measured from this moment forward."},
              {day:"Day 30", color:C.renew, icon:"🧠", title:"First AI report", body:"Upload your Day 30 photo. Our AI compares it to your baseline and generates a personalised report with your Root Score™ and what to focus on next."},
              {day:"Day 60", color:C.rebuild, icon:"📈", title:"Something is happening", body:"Most customers notice visible change between Day 45 and 60. New growth at the hairline. A fuller feel. The AI documents everything it sees."},
              {day:"Day 90", color:C.tc, icon:"🏆", title:"Your transformation", body:"Side by side. Before and after. Ninety days of documented evidence. Enter the Root Challenge. Share your story. Win £10,000."},
            ].map(s=>(
              <div key={s.day} style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.07)",padding:"28px 22px",borderTop:`4px solid ${s.color}`}}>
                <div style={{fontSize:28,marginBottom:12}}>{s.icon}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:300,color:s.color,marginBottom:8}}>{s.day}</div>
                <div style={{fontSize:14,fontWeight:500,color:C.cream,marginBottom:10}}>{s.title}</div>
                <p style={{fontSize:12.5,color:"rgba(246,242,234,.4)",lineHeight:1.85}}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ROOT CHALLENGE ── */}
      <div style={{background:C.tc,padding:P}}>
        <div style={{maxWidth:760,margin:"0 auto",textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>🏆</div>
          <div style={{fontSize:10,letterSpacing:"3px",textTransform:"uppercase",color:"rgba(255,255,255,.6)",fontWeight:500,marginBottom:18}}>The Root Challenge</div>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(36px,5.5vw,62px)",fontWeight:300,lineHeight:1.05,color:"white",marginBottom:18}}>
            90 days.<br/>Real results.<br/>£10,000.
          </h2>
          <p style={{fontSize:"clamp(14px,2vw,17px)",color:"rgba(255,255,255,.85)",lineHeight:1.85,fontWeight:300,maxWidth:520,margin:"0 auto 10px"}}>
            Complete your 90-day journey. Share your transformation. Win part of £10,000 in prizes.
          </p>
          <p style={{fontSize:"clamp(13px,1.7vw,15px)",color:"rgba(255,255,255,.6)",lineHeight:1.85,fontWeight:300,maxWidth:500,margin:"0 auto 40px"}}>
            Every RŌOT customer who completes their Root Progress check-ins is automatically entered. Judged on consistency and documented results — not follower count. Anyone can win.
          </p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:2,marginBottom:16}}>
            {[["🏆","£10,000","Grand prize — best documented transformation"],["🎁","10× Year Supply","Runner-up prizes"],["📸","5 Paid Creator","Brand partnerships"],["✅","How to enter","Purchase RŌOT + complete all 90-day check-ins"]].map(([icon,val,label])=>(
              <div key={label} style={{background:"rgba(0,0,0,.12)",padding:"20px 14px",textAlign:"center"}}>
                <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:"white",marginBottom:6,fontWeight:300}}>{val}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,.55)",lineHeight:1.55}}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SOCIAL PROOF STRIP ── */}
      <div style={{background:C.ink,padding:"clamp(28px,4vw,48px) clamp(20px,6vw,80px)"}}>
        <div style={{maxWidth:880,margin:"0 auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:2}}>
            {[
              {stat:"2,000+", label:"Women already on the waitlist"},
              {stat:"3", label:"Formulas built for your specific biology"},
              {stat:"90", label:"Days. Fully tracked. Nothing guessed."},
              {stat:"£0", label:"Extra cost for Root Progress™ — it's included"},
            ].map(item=>(
              <div key={item.stat} style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.06)",padding:"24px 20px",textAlign:"center"}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:40,fontWeight:300,color:C.tc,marginBottom:8}}>{item.stat}</div>
                <div style={{fontSize:12,color:"rgba(246,242,234,.45)",lineHeight:1.6}}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECOND CTA ── */}
      <div style={{padding:P,background:C.cream}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{fontSize:10,letterSpacing:"3px",textTransform:"uppercase",color:C.tc,fontWeight:500,marginBottom:18,textAlign:"center"}}>First 500 Founding Members Only</div>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(32px,5vw,54px)",fontWeight:300,lineHeight:1.05,textAlign:"center",color:C.ink,marginBottom:16}}>
            The launch offer<br/>won't last 48 hours.
          </h2>
          <p style={{fontSize:14,color:C.muted,lineHeight:1.9,textAlign:"center",marginBottom:10}}>After the first 500 founding members, the offer is gone. Exclusive pricing, the free massager, and priority access all expire at launch.</p>
          <p style={{fontSize:13,color:C.muted,lineHeight:1.9,textAlign:"center",marginBottom:36,fontStyle:"italic"}}>One email. When we launch. Nothing before that.</p>
          <EmailForm label="Claim My Founding Spot"/>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{padding:"clamp(48px,6vw,80px) clamp(20px,6vw,80px)",background:C.warm}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(26px,4vw,40px)",fontWeight:300,marginBottom:40,textAlign:"center",color:C.ink}}>Questions worth answering.</h2>
          {[
            ["Why are there three formulas instead of one?","Because stress-related shedding, hormonal thinning, and post-menopause density loss are three different biological problems. They share the same symptom but almost nothing else. Using the wrong formula is why most serums don't work."],
            ["How do I know which formula is right for me?","When we launch, a short quiz matches you to the right formula based on your specific situation. Join the waitlist and you'll be first to take it."],
            ["When will RŌOT actually launch?","We're in final production. Founding members get emailed the moment we're live — before it's open to anyone else."],
            ["What is Root Progress™ and is it really free?","Yes, completely free with every order. It's a 90-day AI tracking system where you upload scalp photos at Day 1, 30, 60, and 90. Our AI compares them, reviews your check-in data, and generates a personalised report guiding you through the next 30 days. No other hair care brand does this."],
            ["What is the Root Challenge?","Every customer who completes their 90-day Root Progress journey is automatically entered. The most compelling documented transformations win — judged on consistency and results, not social following. The prize fund is £10,000."],
            ["What happens when I refer friends?","Every referral moves you up the waitlist and unlocks rewards. Five referrals gets you a free Scalp Massager. Ten gets you a Founding Member gift box. Twenty-five gets you a free full serum. Your personal link appears the moment you join."],
          ].map(([q,a],i)=><FAQ key={i} q={q} a={a}/>)}
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{background:C.ink,padding:P,textAlign:"center"}}>
        <div style={{maxWidth:560,margin:"0 auto"}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(32px,5vw,58px)",fontWeight:300,lineHeight:1.05,color:C.cream,marginBottom:16}}>
            Your scalp.<br/>Your formula.<br/><em style={{color:C.tc}}>Your 90 days.</em>
          </div>
          <p style={{fontSize:14,color:"rgba(246,242,234,.4)",lineHeight:1.85,marginBottom:10,fontWeight:300}}>Join {socialProof} women already waiting.</p>
          <p style={{fontSize:13,color:"rgba(246,242,234,.3)",lineHeight:1.75,marginBottom:36,fontWeight:300,fontStyle:"italic"}}>One email. When we launch. That's it.</p>
          <div style={{background:"rgba(246,242,234,.05)",border:"1px solid rgba(246,242,234,.1)",padding:"26px",borderRadius:3,marginBottom:28}}>
            <EmailForm dark label="Join the Waitlist" showStage={false}/>
          </div>
          <div style={{display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap"}}>
            {["3 stage formulas","AI progress tracking","90-day system","Free massager · first 500"].map(t=>(
              <div key={t} style={{fontSize:11,color:"rgba(246,242,234,.22)",display:"flex",alignItems:"center",gap:6}}>
                <span style={{color:C.tc}}>✓</span>{t}
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

export default WaitlistPage;